import type { Ref } from "vue";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { CsvFileUpload, FileReaderParams } from "@/common/fileUpload";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  MemberUploadInput,
  UploadDeliveryRequestDocument,
  UploadMemberDocument,
  UploadMemberMutation,
  UploadMemberMutationVariables,
} from "@/generated/graphql";

/**
 * 配達依頼登録開始画面のCSVアップロードに必要な依存をまとめた型。
 */
interface UseDeliveryRequestRegisterStartUploadParams {
  overlay: Ref<boolean>;
  messagedialog: Ref<boolean>;
  message: Ref<string>;
  uploadDialog: Ref<boolean>;
  fileInput: Ref<HTMLInputElement | null>;
  fileInputdelivery: Ref<HTMLInputElement | null>;
}

/**
 * 配達依頼登録開始画面のCSVアップロードを扱う composable。
 */
export const useDeliveryRequestRegisterStartUpload = ({
  overlay,
  messagedialog,
  message,
  uploadDialog,
  fileInput,
  fileInputdelivery,
}: UseDeliveryRequestRegisterStartUploadParams) => {
  // 配達依頼CSVを共通アップロード処理で送信する
  const uploadCsvFile = async (file: File): Promise<void> => {
    const params: FileReaderParams = getCsvFileUploadParams(file);
    uploadDialog.value = true;
    await CsvFileUpload.execute(params);
  };

  // 配達依頼CSVアップロードで使用する共通パラメータを構築する
  const getCsvFileUploadParams = (file: File): FileReaderParams => {
    return {
      file,
      createUploadInput: (base64: string) => ({
        customer_code: localStorageGetCustomerCode(),
        chain_code: localStorageGetChainCode(),
        shop_code: localStorageGetShopCode(),
        csv: base64,
        user_id: localStorageGetUserId(),
      }),
      mutationDocument: UploadDeliveryRequestDocument,
      resultDataKey: "uploadDeliveryRequest",
      csv_split_unit: 15,
      // 完了時は結果メッセージを表示
      handleUploadComplete: (_failed: boolean, msg: string) => {
        messagedialog.value = true;
        message.value = msg;
        uploadDialog.value = false;
      },
      // 失敗時はオーバーレイ解除とエラーメッセージ表示
      handleError: (error: unknown, msg: string) => {
        overlay.value = false;
        messagedialog.value = true;
        message.value = msg;
        uploadDialog.value = false;
        if (error instanceof Error) {
          console.error("エラー:", error.message);
        } else {
          console.error("未知のエラー:", error);
        }
      },
    };
  };

  // 会員CSVを読み込み、Base64化してアップロードする
  const readFileAsync = (file: File): void => {
    const reader = new FileReader();
    uploadDialog.value = true;

    reader.onload = async (e) => {
      if (!e.target || !e.target.result) {
        return;
      }
      const csvData: string = e.target.result as string;
      try {
        // GraphQLの入力形式に合わせてCSVをBase64化
        const tempCsvData = Buffer.from(csvData).toString("base64");
        const input: MemberUploadInput = {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
          csv: tempCsvData,
          user_id: localStorageGetUserId(),
        };

        const apolloClient = await generateApolloClient();
        const result = await apolloClient.query<
          UploadMemberMutation,
          UploadMemberMutationVariables
        >({
          query: UploadMemberDocument,
          variables: { input },
          fetchPolicy: "no-cache",
        });

        if (result.data.uploadMember?.result === "Completed") {
          messagedialog.value = true;
          message.value = "アップロードが完了しました。";
        } else if (result.data.uploadMember?.result === "Error") {
          // 行単位エラーがあれば結合して表示
          messagedialog.value = true;
          message.value = "アップロードに失敗しました。\n";
          if (result.data.uploadMember?.detail) {
            const tmpArr: string[] = [];
            for (const key in result.data.uploadMember.detail) {
              const detail = result.data.uploadMember.detail[key];
              tmpArr.push(detail?.row_number + ":" + detail?.message!.join("") + "\n");
            }
            message.value += tmpArr.join("");
          }
        }
      } catch (err: unknown) {
        console.error(err instanceof Error ? err.message : err);
        overlay.value = false;
        messagedialog.value = true;
        message.value = "アップロードに失敗しました。";
        return;
      } finally {
        uploadDialog.value = false;
      }
    };

    reader.onerror = () => {
      messagedialog.value = true;
      message.value = "ファイルの読取りに失敗しました。";
    };

    // 既存仕様に合わせて Shift-JIS で読み込む
    reader.readAsText(file, "shift-jis");
  };

  // 会員CSV input 変更時ハンドラ
  const onChangeFileUpload = (e: Event): void => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      readFileAsync(file);
    }
    if (fileInput.value) {
      fileInput.value.value = "";
    }
  };

  // 配達依頼CSV input 変更時ハンドラ
  const onChangeFileUploadDelivery = (e: Event): void => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      uploadCsvFile(file);
    }
    if (fileInputdelivery.value) {
      fileInputdelivery.value.value = "";
    }
  };

  // 非表示 input をクリックしてファイル選択ダイアログを開く
  const clickFileInputDelivery = (): void => {
    fileInputdelivery.value?.click();
  };

  return {
    onChangeFileUpload,
    onChangeFileUploadDelivery,
    clickFileInputDelivery,
  };
};
